"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTag } from "@tabler/icons-react";
import type { CategoryItem } from "@/types";
import { cfFetch } from "../../api/apiClient";
import { CategoryKeywordsSection } from "./CategoryKeywordsSection";
import {
  CATEGORY_COLOR_SWATCHES,
  DEFAULT_CATEGORY_COLOR,
} from "./categoryColors";

type Props = {
  category: CategoryItem | null;
  categories: CategoryItem[];
  hasChildren: boolean;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

export function EditCategoryModal({
  category,
  categories,
  hasChildren,
  open,
  onClose,
  onSaved,
}: Props) {
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(DEFAULT_CATEGORY_COLOR);
  const [kind, setKind] = useState<"income" | "expense">("expense");
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const opened = Boolean(open && category);

  useEffect(() => {
    if (!category || !open) return;
    setName(category.name);
    setColor(category.color || DEFAULT_CATEGORY_COLOR);
    setKind(category.type === "income" ? "income" : "expense");
    setSelectedParentId(category.parentCategoryId);
    setError(null);
  }, [category, open]);

  const parentOptions = useMemo(() => {
    if (!category) return [];
    return categories
      .filter(
        (candidate) =>
          candidate.id !== category.id &&
          candidate.type === kind &&
          candidate.parentCategoryId === null,
      )
      .map((candidate) => ({ value: candidate.id, label: candidate.name }));
  }, [categories, category, kind]);

  useEffect(() => {
    if (!selectedParentId) return;
    const parentExists = parentOptions.some((option) => option.value === selectedParentId);
    if (!parentExists) {
      setSelectedParentId(null);
    }
  }, [parentOptions, selectedParentId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!category) return;
    setSaving(true);
    setError(null);
    try {
      const res = await cfFetch(`/categories/${category.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          color,
          type: kind,
          parentCategoryId: selectedParentId,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          detail?: { error?: string } | string;
        };
        const d = body.detail;
        setError(
          typeof d === "string"
            ? d
            : typeof d === "object" && d?.error
              ? d.error
              : "Could not save",
        );
        return;
      }
      onSaved();
      onClose();
    } catch {
      setError("Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="lg"
      radius="md"
      padding="xl"
      closeOnClickOutside={!saving}
      closeOnEscape={!saving}
      title={
        <Group gap="xs">
          <IconTag size={20} stroke={1.5} />
          <Text size="lg" fw={600}>
            Edit category
          </Text>
        </Group>
      }
      styles={{
        content: {
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
        header: {
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: 16,
          marginBottom: 8,
        },
      }}
    >
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="lg">
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            radius="md"
            disabled={saving}
            styles={{
              label: { fontWeight: 500, marginBottom: 8 },
            }}
          />

          <Select
            label="Type"
            data={[
              { value: "income", label: "Income" },
              { value: "expense", label: "Expense" },
            ]}
            value={kind}
            onChange={(v) => setKind(v === "income" ? "income" : "expense")}
            disabled={saving}
            radius="md"
            styles={{
              label: { fontWeight: 500, marginBottom: 8 },
            }}
          />

          <Select
            label="Parent category"
            placeholder="Top level"
            clearable
            data={parentOptions}
            value={selectedParentId}
            onChange={setSelectedParentId}
            disabled={saving || hasChildren}
            radius="md"
            styles={{
              label: { fontWeight: 500, marginBottom: 8 },
            }}
            description={
              hasChildren
                ? "This category has subcategories and cannot be moved under another parent."
                : undefined
            }
          />

          <div>
            <Text size="sm" fw={500} mb={8}>
              Color
            </Text>
            <div className="flex flex-wrap gap-3">
              {CATEGORY_COLOR_SWATCHES.map((swatch) => {
                const isSelected = color.toLowerCase() === swatch.toLowerCase();
                return (
                  <button
                    key={swatch}
                    type="button"
                    onClick={() => setColor(swatch)}
                    disabled={saving}
                    aria-label={`Select ${swatch} color`}
                    className={`h-10 w-10 rounded-full border-2 transition ${
                      isSelected
                        ? "border-slate-900 ring-2 ring-slate-900/20"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    style={{ backgroundColor: swatch }}
                  />
                );
              })}
            </div>
          </div>

          {category ? (
            <CategoryKeywordsSection
              variant="live"
              categoryId={category.id}
              disabled={saving}
            />
          ) : null}

          {error ? (
            <Text size="sm" c="red">
              {error}
            </Text>
          ) : null}

          <Group justify="flex-end" mt="md">
            <Button
              type="button"
              variant="default"
              onClick={onClose}
              disabled={saving}
              radius="md"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              disabled={!name.trim()}
              radius="md"
              styles={{
                root: {
                  backgroundColor: "#030213",
                  "&:hover": { backgroundColor: "#1a1a2e" },
                },
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
